import { FunctionComponent, useState } from "react";
import bienveillanceIcon from "../../assets/values/bienveillance.svg";
import engagementIcon from "../../assets/values/engagement.svg";
import faireEquipeIcon from "../../assets/values/faire-equipe.svg";
import respectIcon from "../../assets/values/respect.svg";
import { DecisionAlert, DecisionSuggestion } from "../../models/DecisionSupport";
import {
    OverviewScope,
    PoleDashboardViewModel,
    ServiceScoreViewModel,
    VALUE_CODES,
    VALUE_LABELS,
    ValueCode,
    ValueScoreViewModel
} from "../../models/OverviewDashboard";
import {
    calculatePoleDecisionAlerts,
    calculateServiceDecisionAlerts
} from "../../services/DecisionSupportService";
import DecisionAlertsPanel from "./DecisionAlertsPanel";
import EvolutionHistoryPanel from "./EvolutionHistoryPanel";
import OrganizationNotesPanel from "./OrganizationNotesPanel";
import styles from "./OverviewWidgets.module.css";

const VALUE_PRESENTATION: Record<ValueCode, {
    icon: string;
    colourClass: string;
}> = {
    bienveillance: {
        icon: bienveillanceIcon,
        colourClass: styles.bienveillance
    },
    engagement: {
        icon: engagementIcon,
        colourClass: styles.engagement
    },
    "faire-equipe": {
        icon: faireEquipeIcon,
        colourClass: styles.faireEquipe
    },
    respect: {
        icon: respectIcon,
        colourClass: styles.respect
    }
};

export const ValueFilterWidget: FunctionComponent<{
    selectedValue: ValueCode;
    onValueChange: (value: ValueCode) => void;
}> = ({ selectedValue, onValueChange }) => (
    <aside className={styles.filterPanel}>
        <h2>Filtres</h2>
        {VALUE_CODES.map((code) => (
            <label key={code} className={`${styles.filterItem} ${VALUE_PRESENTATION[code].colourClass}`}>
                <input
                    type="radio"
                    name="selected-value"
                    checked={selectedValue === code}
                    onChange={() => onValueChange(code)}
                />
                <img src={VALUE_PRESENTATION[code].icon} alt="" aria-hidden="true" />
                <span>{VALUE_LABELS[code]}</span>
            </label>
        ))}
    </aside>
);

type ServicesPanelTab = "services" | "evolution" | "notes" | "alerts";

export const ServicesScoreTableWidget: FunctionComponent<{
    dashboard: PoleDashboardViewModel;
    selectedServiceId?: string;
    selectedValue: ValueCode;
    onServiceSelect: (serviceId: string) => void;
    fhirBaseUrl: string;
    measureId: string;
    scope: OverviewScope;
    onAlertAction?: (alert: DecisionAlert) => void;
}> = ({
    dashboard,
    selectedServiceId,
    selectedValue,
    onServiceSelect,
    fhirBaseUrl,
    measureId,
    scope,
    onAlertAction
}) => {
    const [activeTab, setActiveTab] = useState<ServicesPanelTab>("services");
    const [alertScope, setAlertScope] = useState<OverviewScope>("pole");
    const poleAlerts = calculatePoleDecisionAlerts(dashboard);
    const serviceAlerts = calculateServiceDecisionAlerts(dashboard);
    const visibleAlerts = alertScope === "pole" ? poleAlerts : serviceAlerts;
    const visibleAlertCount = visibleAlerts.length;

    return (
        <section className={styles.servicesPanel}>
            <nav className={styles.servicesTabs} aria-label="Données des services du pôle">
                <button
                    type="button"
                    className={activeTab === "services" ? styles.activeServicesTab : undefined}
                    onClick={() => setActiveTab("services")}
                >
                    Services du pôle ({dashboard.services.length})
                </button>
                <button
                    type="button"
                    className={activeTab === "evolution" ? styles.activeServicesTab : undefined}
                    onClick={() => setActiveTab("evolution")}
                >
                    Évolution
                </button>
                <button
                    type="button"
                    className={activeTab === "notes" ? styles.activeServicesTab : undefined}
                    onClick={() => setActiveTab("notes")}
                >
                    Notes
                </button>
                <button
                    type="button"
                    className={activeTab === "alerts" ? styles.activeServicesTab : undefined}
                    onClick={() => setActiveTab("alerts")}
                >
                    Alertes
                    {visibleAlertCount > 0 && (
                        <span className={styles.alertTabBadge}>{visibleAlertCount}</span>
                    )}
                </button>
            </nav>

            {activeTab === "services" && (
                <ServicesOverviewTable
                    services={dashboard.services}
                    selectedServiceId={selectedServiceId}
                    selectedValue={selectedValue}
                    onServiceSelect={onServiceSelect}
                />
            )}

            {activeTab === "evolution" && (
                <EvolutionHistoryPanel
                    fhirBaseUrl={fhirBaseUrl}
                    measureId={measureId}
                    scope={scope}
                    services={dashboard.services}
                    selectedServiceId={selectedServiceId}
                    poleName={dashboard.pole.name}
                />
            )}

            {activeTab === "notes" && (
                <OrganizationNotesPanel
                    fhirBaseUrl={fhirBaseUrl}
                    organizationReference={dashboard.pole.reference}
                    organizationName={dashboard.pole.name}
                />
            )}

            {activeTab === "alerts" && (
                <DecisionAlertsPanel
                    alerts={visibleAlerts}
                    alertScope={alertScope}
                    poleCount={poleAlerts.length}
                    servicesCount={serviceAlerts.length}
                    onScopeChange={setAlertScope}
                    onAction={onAlertAction}
                />
            )}
        </section>
    );
};

const ServicesOverviewTable: FunctionComponent<{
    services: ServiceScoreViewModel[];
    selectedServiceId?: string;
    selectedValue: ValueCode;
    onServiceSelect: (serviceId: string) => void;
}> = ({ services, selectedServiceId, selectedValue, onServiceSelect }) => (
    <div className={styles.tableWrapper}>
        <table className={styles.servicesTable}>
            <thead>
                <tr>
                    <th>Service</th>
                    {VALUE_CODES.map((code) => (
                        <th key={code} className={styles.compactValueHeader}>
                            <span
                                className={`${styles.valueIconBox} ${VALUE_PRESENTATION[code].colourClass} ${selectedValue === code ? styles.selectedValueHeader : ""}`}
                                title={VALUE_LABELS[code]}
                            >
                                <img src={VALUE_PRESENTATION[code].icon} alt={VALUE_LABELS[code]} />
                            </span>
                        </th>
                    ))}
                    <th className={styles.evolutionHeader}>Évolution</th>
                    <th className={styles.averageHeader}>Moyenne</th>
                </tr>
            </thead>
            <tbody>
                {services.map((item) => (
                    <ServiceStatsRow
                        key={item.service.id}
                        item={item}
                        selected={selectedServiceId === item.service.id}
                        selectedValue={selectedValue}
                        onSelect={() => onServiceSelect(item.service.id)}
                    />
                ))}
            </tbody>
        </table>
    </div>
);

const ServiceStatsRow: FunctionComponent<{
    item: ServiceScoreViewModel;
    selected: boolean;
    selectedValue: ValueCode;
    onSelect: () => void;
}> = ({ item, selected, selectedValue, onSelect }) => (
    <tr
        className={selected ? styles.selectedRow : undefined}
        onClick={onSelect}
    >
        <td className={styles.serviceNameCell}>{item.service.name}</td>
        {VALUE_CODES.map((code) => (
            <td key={code} className={styles.valueScoreCell}>
                <ValueScoreCompact
                    value={item.values[code]}
                    code={code}
                    active={selectedValue === code}
                />
            </td>
        ))}
        <td>
            <EvolutionCell
                value={item.evolution}
                direction={item.evolutionDirection}
            />
        </td>
        <td>
            <AverageScoreCell score={item.averageScore} />
        </td>
    </tr>
);

const ValueScoreCompact: FunctionComponent<{
    value: ValueScoreViewModel;
    code: ValueCode;
    active?: boolean;
}> = ({ value, code, active }) => {
    const percentage = getValuePercentage(value);

    return (
        <div className={`${styles.compactScore} ${VALUE_PRESENTATION[code].colourClass} ${active ? styles.activeCompactScore : ""}`}>
            <span className={styles.compactScoreValue}>
                {percentage === undefined ? "N/A" : Math.round(percentage)}
            </span>
            <span className={styles.compactScoreTrack} aria-hidden="true">
                {percentage !== undefined && <span style={{ width: `${percentage}%` }} />}
            </span>
        </div>
    );
};

const EvolutionCell: FunctionComponent<{
    value?: number;
    direction?: ServiceScoreViewModel["evolutionDirection"];
}> = ({ value, direction }) => {
    if (value === undefined || !direction || direction === "no-data") {
        return <span className={styles.evolutionNoData}>N/A</span>;
    }

    const symbol = direction === "up" ? "↗" : direction === "down" ? "↘" : "→";
    const sign = value > 0 ? "+" : "";

    return (
        <span className={`${styles.evolutionValue} ${styles[`evolution_${direction}`]}`}>
            {symbol} {sign}{value}
        </span>
    );
};

const AverageScoreCell: FunctionComponent<{ score?: number }> = ({ score }) => {
    const percentage = score === undefined
        ? undefined
        : Math.min(Math.max(score * 10, 0), 100);

    return (
        <div className={styles.averageScore}>
            <span className={styles.averageTrack} aria-hidden="true">
                {percentage !== undefined && <span style={{ width: `${percentage}%` }} />}
            </span>
            <strong>{percentage === undefined ? "N/A" : Math.round(percentage)}</strong>
        </div>
    );
};

interface ScoreDetailsWidgetProps {
    title: string;
    values: Record<ValueCode, ValueScoreViewModel>;
    selectedValue: ValueCode;
    subtitle?: string;
    onValueChange?: (value: ValueCode) => void;
}

export const ScoreDetailsWidget: FunctionComponent<ScoreDetailsWidgetProps> = ({
    title,
    values,
    selectedValue,
    subtitle,
    onValueChange
}) => (
    <section className={styles.sideCard}>
        <h2>{title}</h2>
        {subtitle && <p className={styles.contextLabel}>{subtitle}</p>}
        {onValueChange && <p className={styles.scoreHint}>Cliquez sur une valeur pour la sélectionner.</p>}
        <div className={styles.scoreGrid}>
            {VALUE_CODES.map((code) => {
                const value = values[code];
                const percentage = getValuePercentage(value);
                const hasData = value.status === "available" && value.score !== undefined && percentage !== undefined;
                const presentation = VALUE_PRESENTATION[code];
                const selected = selectedValue === code;

                return (
                    <article
                        key={code}
                        className={`${styles.scoreTile} ${presentation.colourClass} ${selected ? styles.activeScore : ""} ${onValueChange ? styles.clickableScore : ""}`}
                        role={onValueChange ? "button" : undefined}
                        tabIndex={onValueChange ? 0 : undefined}
                        aria-pressed={onValueChange ? selected : undefined}
                        aria-label={onValueChange ? `Sélectionner ${VALUE_LABELS[code]}` : undefined}
                        onClick={() => onValueChange?.(code)}
                        onKeyDown={(event) => {
                            if (!onValueChange || (event.key !== "Enter" && event.key !== " ")) return;
                            event.preventDefault();
                            onValueChange(code);
                        }}
                    >
                        <div className={styles.scoreValue}>
                            <strong>{hasData ? value.score?.toFixed(1) : "N/A"}</strong>
                            {hasData && <span>/10</span>}
                        </div>
                        <div
                            className={styles.progress}
                            aria-label={`${VALUE_LABELS[code]} : ${hasData ? `${percentage.toFixed(0)} %` : "N/A"}`}
                        >
                            {hasData && <span style={{ width: `${percentage}%` }} />}
                        </div>
                        <div className={styles.scoreName}>
                            <img src={presentation.icon} alt="" aria-hidden="true" />
                            <span>{VALUE_LABELS[code]}</span>
                        </div>
                    </article>
                );
            })}
        </div>
    </section>
);

export const ServiceScoreDetailsWidget: FunctionComponent<{
    service?: ServiceScoreViewModel;
    selectedValue: ValueCode;
    onValueChange?: (value: ValueCode) => void;
}> = ({ service, selectedValue, onValueChange }) => {
    if (!service) {
        return (
            <section className={styles.sideCard}>
                <h2>Service</h2>
                <p className={styles.emptyText}>Sélectionnez un service pour consulter ses résultats.</p>
            </section>
        );
    }

    return (
        <ScoreDetailsWidget
            title={service.service.name}
            values={service.values}
            selectedValue={selectedValue}
            subtitle="Statistiques du service"
            onValueChange={onValueChange}
        />
    );
};

export const PoleScoreDetailsWidget: FunctionComponent<{
    dashboard: PoleDashboardViewModel;
    selectedValue: ValueCode;
    onValueChange?: (value: ValueCode) => void;
}> = ({ dashboard, selectedValue, onValueChange }) => (
    <ScoreDetailsWidget
        title={dashboard.pole.name}
        values={dashboard.poleScores}
        selectedValue={selectedValue}
        subtitle={`Moyenne calculée sur ${dashboard.services.length} service(s)`}
        onValueChange={onValueChange}
    />
);

export const ActionPlanSummaryWidget: FunctionComponent = () => (
    <section className={styles.sideCard}>
        <h2>Plan d'action</h2>
        <p className={styles.emptyText}>Consultez l'onglet Accompagnement pour voir les actions engagées.</p>
    </section>
);

/**
 * Conservé temporairement pour compatibilité avec d'éventuels imports externes.
 * Les suggestions du dashboard sont maintenant regroupées dans l'onglet Alertes.
 */
export const SuggestionsWidget: FunctionComponent<{
    suggestion: DecisionSuggestion;
    onAction?: (suggestion: DecisionSuggestion) => void;
}> = ({ suggestion, onAction }) => (
    <section className={`${styles.sideCard} ${styles.suggestions}`}>
        <h2>Suggestions</h2>
        <strong className={styles.suggestionTitle}>{suggestion.title}</strong>
        <p className={styles.suggestionText}>{suggestion.description}</p>
        {suggestion.actionLabel && (
            <button type="button" onClick={() => onAction?.(suggestion)}>
                {suggestion.actionLabel}
            </button>
        )}
    </section>
);

export const getSelectedService = (
    dashboard: PoleDashboardViewModel,
    serviceId?: string
): ServiceScoreViewModel | undefined => dashboard.services.find(
    (service) => service.service.id === serviceId
);

const getValuePercentage = (value: ValueScoreViewModel): number | undefined => {
    if (typeof value.percentage === "number" && Number.isFinite(value.percentage)) {
        return Math.min(Math.max(value.percentage, 0), 100);
    }

    if (typeof value.score === "number" && Number.isFinite(value.score)) {
        return Math.min(Math.max(value.score * 10, 0), 100);
    }

    return undefined;
};
